import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Library } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { useWorkingContext } from '@/hooks/useWorkingContext';
import { useCampusPicker } from '@/hooks/usePickers';
import { gradeOfferingsApi } from '@/services/api/endpoints/academic';
import { feeCategoriesApi, feeStructuresApi } from '@/services/api/endpoints/finance';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { FeeStructure, FinanceStatus } from '@/types/finance';
import { Money } from './shared';

const FREQUENCIES = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'SEMESTER', label: 'Per semester' },
  { value: 'TERM', label: 'Per term' },
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'ONE_TIME', label: 'One-off' },
  { value: 'CUSTOM', label: 'Custom' },
];

const RECURRING = new Set(['MONTHLY', 'QUARTERLY', 'SEMESTER', 'ANNUAL']);

const schema = z
  .object({
    feeCategoryId: z.string().uuid('Choose what kind of fee this is'),
    campusId: z.string(),
    gradeOfferingId: z.string(),
    name: z.string().min(1, 'A name is required').max(150),
    description: z.string().max(500).optional(),
    amount: z.coerce.number().min(0, 'An amount cannot be negative'),
    frequency: z.string().min(1),
    dueDayOfMonth: z.string(),
    dueAfterDays: z.string(),
    effectiveFrom: z.string().min(1, 'A start date is required'),
    effectiveTo: z.string(),
    mandatory: z.boolean(),
    amountOverridable: z.boolean(),
    taxable: z.boolean(),
    taxPercentage: z.string(),
  })
  .refine((v) => v.dueDayOfMonth === '' || v.dueAfterDays === '', {
    path: ['dueAfterDays'],
    message: 'Set a day of the month or a number of days after issue, not both',
  })
  .refine((v) => !RECURRING.has(v.frequency) || v.dueDayOfMonth !== '' || v.dueAfterDays !== '', {
    path: ['dueDayOfMonth'],
    message: 'A recurring fee needs a due-date rule, or every instalment falls due the day it is raised',
  })
  .refine((v) => !v.taxable || v.taxPercentage !== '', {
    path: ['taxPercentage'],
    message: 'A taxable fee needs a tax percentage',
  });

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  feeCategoryId: '', campusId: '', gradeOfferingId: '', name: '', description: '',
  amount: 0, frequency: 'MONTHLY', dueDayOfMonth: '10', dueAfterDays: '',
  effectiveFrom: '', effectiveTo: '', mandatory: true, amountOverridable: false,
  taxable: false, taxPercentage: '',
};

/**
 * The price list.
 *
 * <p>A structure is not a charge — it is what a class costs. Changing one alters
 * what will be billed from now on and never what has already been billed, which
 * the screen says out loud when editing an entry that students are already
 * assigned to.
 */
export function FeeStructuresPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('name,asc');
  const context = useWorkingContext();

  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<FinanceStatus | ''>('');
  const [editing, setEditing] = useState<FeeStructure | null>(null);
  const [open, setOpen] = useState(false);

  const campuses = useCampusPicker();
  const categories = useQuery({
    queryKey: queryKeys.feeCategories.list(true),
    queryFn: () => feeCategoriesApi.list(true),
  });

  const params = {
    ...table.params,
    ...(context.academicYearId !== null ? { academicYearId: context.academicYearId } : {}),
    ...(categoryFilter !== '' ? { feeCategoryId: categoryFilter } : {}),
    ...(statusFilter !== '' ? { status: statusFilter } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.feeStructures.list(params),
    queryFn: () => feeStructuresApi.list(params),
  });

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });
  const watchCampus = form.watch('campusId');

  const offerings = useQuery({
    queryKey: queryKeys.gradeOfferings.list({ picker: true, year: context.academicYearId, campus: watchCampus }),
    queryFn: () =>
      gradeOfferingsApi.list({
        page: 0, size: 200, status: 'ACTIVE',
        ...(context.academicYearId !== null ? { academicYearId: context.academicYearId } : {}),
        ...(watchCampus !== '' ? { campusId: watchCampus } : {}),
      }),
    enabled: open,
  });

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = {
        feeCategoryId: values.feeCategoryId,
        academicYearId: context.academicYearId as string,
        ...(values.campusId !== '' ? { campusId: values.campusId } : {}),
        ...(values.gradeOfferingId !== '' ? { gradeOfferingId: values.gradeOfferingId } : {}),
        name: values.name,
        ...(values.description !== undefined && values.description !== ''
          ? { description: values.description } : {}),
        amount: values.amount,
        frequency: values.frequency,
        ...(values.dueDayOfMonth !== '' ? { dueDayOfMonth: Number(values.dueDayOfMonth) } : {}),
        ...(values.dueAfterDays !== '' ? { dueAfterDays: Number(values.dueAfterDays) } : {}),
        effectiveFrom: values.effectiveFrom,
        ...(values.effectiveTo !== '' ? { effectiveTo: values.effectiveTo } : {}),
        mandatory: values.mandatory,
        amountOverridable: values.amountOverridable,
        taxable: values.taxable,
        ...(values.taxable && values.taxPercentage !== ''
          ? { taxPercentage: Number(values.taxPercentage) } : {}),
      };
      return editing === null
        ? feeStructuresApi.create(payload)
        : feeStructuresApi.update(editing.id, payload);
    },
    onSuccess: () => {
      toast.success(editing === null ? 'Added to the price list' : 'Price list updated');
      setOpen(false);
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.feeStructures.all });
    },
    onError: (error) => handleApiError(error, form.setError),
  });

  const changeStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: FinanceStatus }) =>
      feeStructuresApi.changeStatus(id, { status }),
    onSuccess: () => {
      toast.success('Price list entry updated');
      void queryClient.invalidateQueries({ queryKey: queryKeys.feeStructures.all });
    },
    onError: (error) => handleApiError(error),
  });

  function openCreate() {
    setEditing(null);
    form.reset({ ...EMPTY, campusId: context.campusId ?? '' });
    setOpen(true);
  }

  function openEdit(structure: FeeStructure) {
    setEditing(structure);
    form.reset({
      feeCategoryId: structure.feeCategoryId,
      campusId: structure.campusId ?? '',
      gradeOfferingId: structure.gradeOfferingId ?? '',
      name: structure.name,
      description: structure.description ?? '',
      amount: structure.amount,
      frequency: structure.frequency,
      dueDayOfMonth: structure.dueDayOfMonth === undefined ? '' : String(structure.dueDayOfMonth),
      dueAfterDays: structure.dueAfterDays === undefined ? '' : String(structure.dueAfterDays),
      effectiveFrom: structure.effectiveFrom,
      effectiveTo: structure.effectiveTo ?? '',
      mandatory: structure.mandatory,
      amountOverridable: structure.amountOverridable,
      taxable: structure.taxable,
      taxPercentage: structure.taxPercentage === undefined ? '' : String(structure.taxPercentage),
    });
    setOpen(true);
  }

  const columns: Column<FeeStructure>[] = [
    {
      key: 'name',
      header: 'Fee',
      sortKey: 'name',
      render: (s) => (
        <div>
          <span className="font-medium text-ink">{s.name}</span>
          <span className="block text-[11px] text-muted">{s.feeCategoryName}</span>
        </div>
      ),
    },
    {
      key: 'scope',
      header: 'Applies to',
      render: (s) => (
        <div className="text-[13px]">
          <span className="text-ink">{s.gradeName ?? 'Every class'}</span>
          <span className="block text-[11px] text-muted">{s.campusName ?? 'Every campus'}</span>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      sortKey: 'amount',
      className: 'text-right',
      render: (s) => <Money amount={s.amount} bold />,
    },
    {
      key: 'frequency',
      header: 'Frequency',
      sortKey: 'frequency',
      render: (s) => (
        <div className="text-[13px]">
          <span className="text-ink">
            {FREQUENCIES.find((f) => f.value === s.frequency)?.label ?? s.frequency}
          </span>
          <span className="block text-[11px] text-muted">
            {s.dueDayOfMonth !== undefined
              ? `due on the ${s.dueDayOfMonth}${s.dueDayOfMonth === 1 ? 'st' : s.dueDayOfMonth === 2 ? 'nd' : s.dueDayOfMonth === 3 ? 'rd' : 'th'}`
              : s.dueAfterDays !== undefined
                ? `due ${s.dueAfterDays} days after issue`
                : 'due on issue'}
          </span>
        </div>
      ),
    },
    {
      key: 'flags',
      header: '',
      render: (s) => (
        <div className="flex flex-wrap gap-1">
          {s.mandatory
            ? <Badge tone="info">assigned automatically</Badge>
            : <Badge tone="neutral">optional</Badge>}
          {s.amountOverridable && <Badge tone="neutral">amount can vary</Badge>}
          {s.taxable && <Badge tone="neutral">+{s.taxPercentage}% tax</Badge>}
        </div>
      ),
    },
    {
      key: 'assigned',
      header: 'Students',
      className: 'text-right',
      render: (s) => <span className="block text-right tabular-nums text-muted">{s.assignedCount}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (s) => (
        <Badge tone={s.status === 'ACTIVE' ? 'success' : 'neutral'}>
          <span className="h-1.5 w-1.5 bg-current" aria-hidden="true" />
          {s.status.toLowerCase()}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (s) => (
        <div className="flex justify-end gap-1">
          {can(PERMISSIONS.FEE_STRUCTURE_MANAGE) && (
            <>
              <Button size="sm" variant="ghost" onClick={() => openEdit(s)}>Edit</Button>
              <Button size="sm" variant="ghost" loading={changeStatus.isPending}
                      onClick={() => changeStatus.mutate({
                        id: s.id,
                        status: s.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
                      })}>
                {s.status === 'ACTIVE' ? 'Retire' : 'Restore'}
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <ListPage
        title="Fee Structures"
        description="What each class is charged, how much, and how often. Changing a price affects future billing only."
        icon={<Library className="h-5 w-5" aria-hidden="true" />}
        action={
          can(PERMISSIONS.FEE_STRUCTURE_MANAGE) ? (
            <Button onClick={openCreate}>New fee</Button>
          ) : undefined
        }
        search={{ value: table.searchInput, onChange: table.setSearchInput, placeholder: 'Search fees…' }}
        filters={
          <>
            <FilterSelect label="Category" value={categoryFilter}
                          onChange={(value) => { setCategoryFilter(value); table.setPage(0); }}
                          options={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))} />
            <FilterSelect label="Status" value={statusFilter}
                          onChange={(value) => { setStatusFilter(value); table.setPage(0); }}
                          options={[{ value: 'ACTIVE', label: 'Active' }, { value: 'INACTIVE', label: 'Retired' }]} />
          </>
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(s) => s.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="Nothing on the price list"
          emptyDescription="Add what each class is charged before running the billing."
          emptyIcon={<Library className="h-6 w-6" aria-hidden="true" />}
        />
      </ListPage>

      <Modal
        open={open}
        size="lg"
        title={editing === null ? 'New fee' : `Edit ${editing.name}`}
        description="Scope narrows outwards: one class, one campus, or the whole school."
        onClose={() => { setOpen(false); setEditing(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => { setOpen(false); setEditing(null); }}>
              Cancel
            </Button>
            <Button loading={save.isPending}
                    onClick={form.handleSubmit((values) => save.mutate(values))}>
              Save
            </Button>
          </>
        }
      >
        <form className="space-y-4" noValidate
              onSubmit={form.handleSubmit((values) => save.mutate(values))}>
          {editing !== null && editing.assignedCount > 0 && (
            <p className="border border-line bg-surface-2 px-3.5 py-2.5 text-xs text-muted">
              {editing.assignedCount} student{editing.assignedCount === 1 ? ' is' : 's are'} already
              assigned this fee. They keep the amount they were assigned at — a new price applies
              to future assignments only, and issued invoices are untouched.
            </p>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField label="Kind of fee" required disabled={editing !== null}
                         hint={editing !== null ? 'Fixed once the fee exists.' : undefined}
                         error={form.formState.errors.feeCategoryId?.message}
                         {...form.register('feeCategoryId')}>
              <option value="">Choose…</option>
              {(categories.data ?? []).map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </SelectField>
            <TextField label="Name" required placeholder="Grade 5 Tuition"
                       error={form.formState.errors.name?.message}
                       {...form.register('name')} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField label="Campus" disabled={editing !== null}
                         hint="Blank means every campus."
                         {...form.register('campusId')}>
              <option value="">Every campus</option>
              {(campuses.data ?? []).map((campus) => (
                <option key={campus.id} value={campus.id}>{campus.name}</option>
              ))}
            </SelectField>
            <SelectField label="Class" disabled={editing !== null}
                         hint="Blank means every class in the year."
                         {...form.register('gradeOfferingId')}>
              <option value="">Every class</option>
              {(offerings.data?.data ?? []).map((offering) => (
                <option key={offering.id} value={offering.id}>
                  {offering.grade.name} · {offering.campusName ?? 'All campuses'}
                </option>
              ))}
            </SelectField>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <TextField label="Amount" type="number" step="0.01" required
                       error={form.formState.errors.amount?.message}
                       {...form.register('amount')} />
            <SelectField label="How often" required {...form.register('frequency')}>
              {FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </SelectField>
            <TextField label="Due on the" type="number" min="1" max="28"
                       hint="Day of the month. Capped at 28."
                       error={form.formState.errors.dueDayOfMonth?.message}
                       {...form.register('dueDayOfMonth')} />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <TextField label="Or days after issue" type="number" min="0"
                       error={form.formState.errors.dueAfterDays?.message}
                       {...form.register('dueAfterDays')} />
            <TextField label="In force from" type="date" required
                       error={form.formState.errors.effectiveFrom?.message}
                       {...form.register('effectiveFrom')} />
            <TextField label="Until" type="date" hint="Blank means indefinitely."
                       {...form.register('effectiveTo')} />
          </div>

          <div className="space-y-2">
            <Check label="Assigned automatically on enrolment"
                   detail="Mandatory fees are given to every child in the class. Leave off for optional fees such as transport."
                   {...form.register('mandatory')} />
            <Check label="The amount may vary per student"
                   detail="Allows a negotiated amount at assignment. Off means the price list stands and a concession must be recorded as a discount."
                   {...form.register('amountOverridable')} />
            <Check label="Taxable" detail="Adds tax to each instalment."
                   {...form.register('taxable')} />
          </div>

          {form.watch('taxable') && (
            <TextField label="Tax percentage" type="number" step="0.01" required
                       error={form.formState.errors.taxPercentage?.message}
                       {...form.register('taxPercentage')} />
          )}

          <TextField label="Description" placeholder="Anything staff should know about this fee"
                     {...form.register('description')} />
        </form>
      </Modal>
    </>
  );
}

const Check = ({ label, detail, ...rest }: { label: string; detail: string } & React.InputHTMLAttributes<HTMLInputElement>) => (
  <label className="flex items-start gap-2.5 border border-line bg-surface px-3.5 py-2.5">
    <input type="checkbox" className="mt-0.5 h-4 w-4 border-line" {...rest} />
    <span>
      <span className="block text-[13px] font-medium text-ink">{label}</span>
      <span className="block text-xs text-muted">{detail}</span>
    </span>
  </label>
);
